import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { ModalContainer } from '../../../../../Modals/ModalContainer'
import { hideModal } from '../../../../../../redux/actions'
import { useDispatch } from 'react-redux'
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material'
import { MUIFormCheckboxInput } from '../../../../../@Common/Form/CheckboxInput/FormCheckboxInput'
import useIntegrations from './useIntegrations'
import { getStoreDisplayName } from './storeUtils'
import moment from 'moment'

const ManageConnectionModal = ({
  partnerListingId,
  integration,
  app,
  manufacturerStores = [],
  orphanedStoreIds = [],
}) => {
  const dispatch = useDispatch()
  const { updatePartnerListingGovxShopIntegration } = useIntegrations(partnerListingId)
  const [warningOpen, setWarningOpen] = useState(false)
  const [pendingUncheck, setPendingUncheck] = useState(null)

  const orphanedSet = useMemo(() => new Set(orphanedStoreIds.map(String)), [orphanedStoreIds])

  const buildDefaultValues = () => {
    const defaults = {}
    manufacturerStores.forEach((store) => {
      const isLinked = integration.storeIds?.some(id => String(id) === String(store.storeId))
      defaults[`store_${store.storeId}`] = !!isLinked
    })
    return defaults
  }

  const { control, getValues, setValue } = useForm({
    defaultValues: buildDefaultValues(),
  })

  const getSelectedStoreIds = () => {
    const values = getValues()
    return manufacturerStores
      .filter((store) => values[`store_${store.storeId}`])
      .map((store) => store.storeId)
  }

  const handleStoreChange = useCallback((storeId, checked) => {
    if (!checked && orphanedSet.has(String(storeId))) {
      setValue(`store_${storeId}`, true)
      setPendingUncheck(storeId)
      setWarningOpen(true)
    }
  }, [orphanedSet, setValue])

  const confirmUncheck = () => {
    if (pendingUncheck != null) {
      setValue(`store_${pendingUncheck}`, false)
    }
    setWarningOpen(false)
    setPendingUncheck(null)
  }

  const cancelUncheck = () => {
    setWarningOpen(false)
    setPendingUncheck(null)
  }

  const handleSave = async () => {
    const storeIds = getSelectedStoreIds()
    try {
      await updatePartnerListingGovxShopIntegration(
        integration.manufacturerId,
        storeIds,
      )
      dispatch(hideModal())
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <ModalContainer
      title="Manage Connection"
      drawer
      actions={{
        primaryAction: {
          label: 'Save',
          onClick: handleSave,
        },
        secondaryAction: {
          label: 'Close',
          onClick: () => dispatch(hideModal()),
        },
      }}
    >
      {/* Manufacturer Info */}
      <Box sx={{ backgroundColor: '#f3f3f3', borderRadius: '4px', p: 2.5, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          {app?.imageUrl && (
            <Box
              component="img"
              src={app.imageUrl}
              alt={app.name}
              sx={{ width: 56, height: 56, objectFit: 'contain' }}
            />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.4, m: 0 }}>
              {integration.manufacturerName || app?.name} ({integration.manufacturerId})
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', lineHeight: 1.4, m: 0 }}>
              Connected {moment(integration.dateLinkedUtc).format('MM/DD/YYYY')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stores */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', p: 2.5 }}>
        {manufacturerStores.length > 0 ? (
          <Box display="flex" alignItems="flex-start" gap={4}>
            <Box sx={{ width: '45%', flexShrink: 0 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 0.5 }}>Stores</Typography>
              <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.5 }}>
                Select the stores from which you want to pull products for this seller.
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column">
              {manufacturerStores.map((store) => (
                <MUIFormCheckboxInput
                  key={store.storeId}
                  name={`store_${store.storeId}`}
                  label={getStoreDisplayName(store.storeName)}
                  control={control}
                  onChange={(checked) => handleStoreChange(store.storeId, checked)}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: '#666' }}>
            No stores related to this manufacturer through any active product variants.
          </Typography>
        )}
      </Box>

      <Dialog open={warningOpen} onClose={cancelUncheck}>
        <DialogTitle sx={{
          borderBottom: '1px solid #e0e0e0',
          lineHeight: '1.6',
          fontSize: '1.25rem',
          fontFamily: 'Roboto Bold, Helvetica, sans-serif',
          padding: '24px',
          color: 'rgba(0, 0, 0, 0.87)',
        }}>Remove Store</DialogTitle>
        <DialogContent sx={{
          fontFamily: '"Roboto Regular", Helvetica, sans-serif',
          color: 'rgba(0, 0, 0, 0.87)',
          lineHeight: '1.5',
          padding: '24px',
        }}>
          <DialogContentText sx={{
            fontFamily: 'inherit',
            color: 'inherit',
            lineHeight: 'inherit',
            paddingTop: '8px',
          }}>
            Stores that aren&apos;t mapped to a Manufacturer and have no active products will no longer be displayed here.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px 16px' }}>
          <Button
            variant="text"
            onClick={cancelUncheck}
            sx={{
              fontFamily: 'Roboto Medium, Helvetica, sans-serif',
              fontSize: '0.875rem',
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          >Cancel</Button>
          <Button onClick={confirmUncheck} color="primary" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </ModalContainer>
  )
}

ManageConnectionModal.propTypes = {
  partnerListingId: PropTypes.string.isRequired,
  integration: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  manufacturerStores: PropTypes.array,
  orphanedStoreIds: PropTypes.array,
}

export default ManageConnectionModal
